import React, { Component } from 'react'
import { Col, Row } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from "@mui/material";
import { formatAmount } from '../../helper';
import SectionHeader from '../../Pages/admin/shared/SectionHeader';

/* Shop Partnership viewer — BY SHOP.
 *
 * One block per shop: the partners with their percentages, then the card holders. Shops with
 * nothing are listed too, so a shop that genuinely has no partner is distinguishable from one
 * that was filtered out.
 *
 * The TOTAL and the LIST always reconcile, which is the whole point of this screen. A partner row
 * can point at a deleted account (no foreign key on PS_ACC_ID), and the old reporting query hid
 * those because CONCAT of a NULL title is NULL and GROUP_CONCAT skips NULLs — so a shop showed a
 * total its own breakdown could not reach. Here an orphan is a visible red row instead.
 */
const HEAD = { background: '#f1f1f1', color: '#111827', fontWeight: 600 };

class ByShop extends Component {
    render() {
        const { rows, asAtLabel } = this.props;

        return (
            <section>
                <SectionHeader title={<>{asAtLabel}</>} />

                {rows.length === 0 && (
                    <Row><Col md={12}>
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            No shops to show.
                        </div>
                    </Col></Row>
                )}

                {rows.map((shop) => (
                    <Row key={shop.ShopID} style={{ marginBottom: 18 }}>
                        <Col md={12}>
                            <div style={{ padding: '10px 4px 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <strong style={{ color: '#4842a7', fontSize: 15 }}>{shop.ShopName}</strong>
                                <span style={{ color: '#64748b', fontSize: 13 }}>
                                    {shop.partners.length} partner{shop.partners.length === 1 ? '' : 's'}
                                </span>
                                {shop.partners.length > 0 && (
                                    <Chip size="small" label={`Total ${formatAmount(shop.totalPct)}%`}
                                        /* Over 100% on a single day means more than the whole
                                           profit is pledged — worth seeing at a glance. */
                                        color={shop.totalPct > 100 ? 'error' : 'default'} />
                                )}
                                {shop.orphanCount > 0 && (
                                    <Chip size="small" color="error"
                                        label={`${shop.orphanCount} deleted account${shop.orphanCount === 1 ? '' : 's'}`} />
                                )}
                            </div>

                            <Paper>
                                <TableContainer className="report-table-container">
                                    <Table className="report-table" size="small" aria-label={shop.ShopName}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={HEAD}>Holder</TableCell>
                                                <TableCell style={HEAD}>Type</TableCell>
                                                <TableCell style={{ ...HEAD, textAlign: 'right' }}>Percentage</TableCell>
                                                <TableCell style={HEAD}>From</TableCell>
                                                <TableCell style={HEAD}>To</TableCell>
                                                <TableCell style={HEAD}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {shop.partners.length === 0 && shop.cards.length === 0 && (
                                                <TableRow className="table-rows">
                                                    <TableCell colSpan={6} style={{ color: '#64748b' }}>
                                                        No partnership or card on this shop.
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {shop.partners.map((p) => (
                                                <TableRow className="table-rows" key={'p' + p.RowID}
                                                    sx={{ '&:last-child td': { border: 0 } }}>
                                                    <TableCell className="report-label-cell"
                                                        style={+p.IsOrphan ? { color: '#ba1a1a', fontWeight: 600 } : {}}>
                                                        {p.AccountTitle}
                                                    </TableCell>
                                                    <TableCell>Partner</TableCell>
                                                    <TableCell style={{ textAlign: 'right' }}>
                                                        <strong>{formatAmount(p.Pct)}%</strong>
                                                    </TableCell>
                                                    <TableCell>{p.WindowFrom}</TableCell>
                                                    <TableCell>{p.WindowTo}</TableCell>
                                                    <TableCell>
                                                        {+p.IsOrphan
                                                            ? <span style={{ color: '#ba1a1a' }}>account deleted</span>
                                                            : (+p.LiveToday ? 'live' : 'not current')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {/* Cards carry no percentage — the column stays blank
                                                rather than showing a misleading 0.00%. */}
                                            {shop.cards.map((c) => (
                                                <TableRow className="table-rows" key={'c' + c.RowID}
                                                    sx={{ '&:last-child td': { border: 0 } }}>
                                                    <TableCell className="report-label-cell"
                                                        style={+c.IsOrphan ? { color: '#ba1a1a', fontWeight: 600 } : {}}>
                                                        {c.AccountTitle}
                                                        {shop.bothIds.includes(String(c.AccountID)) && (
                                                            <span style={{ color: '#92400e', fontSize: 11, marginLeft: 6 }}>
                                                                also a partner here
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>Card</TableCell>
                                                    <TableCell style={{ textAlign: 'right', color: '#94a3b8' }}>—</TableCell>
                                                    <TableCell>{c.WindowFrom}</TableCell>
                                                    <TableCell>{c.WindowTo}</TableCell>
                                                    <TableCell>
                                                        {+c.IsOrphan
                                                            ? <span style={{ color: '#ba1a1a' }}>account deleted</span>
                                                            : (+c.LiveToday ? 'live' : 'not current')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {shop.partners.length > 0 && (
                                                <TableRow className="table-rows" style={{ background: '#e7e7e7' }}>
                                                    <TableCell><strong>Total pledged</strong></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell style={{ textAlign: 'right' }}>
                                                        <strong>{formatAmount(shop.totalPct)}%</strong>
                                                    </TableCell>
                                                    <TableCell colSpan={3} style={{ color: '#64748b' }}>
                                                        shop keeps {formatAmount(100 - shop.totalPct)}%
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Col>
                    </Row>
                ))}
            </section>
        );
    }
}

export default ByShop;
